<template>
  <dcModal
    :modalShown="show"
    @close-modal="close"
    modalCenter
    :modalClasses="['mapping-save-modal']"
  >
    <template #header>
      <h3 class="modal-title">Save Column Mapping</h3>
      <button class="close-modal btn-close" aria-label="Close">&times;</button>
    </template>

    <template #body>
      <div class="form-group">
        <label for="mapping-name">Mapping Name</label>
        <input
          id="mapping-name"
          v-model="mappingName"
          type="text"
          class="form-control"
          :class="{ 'is-invalid': nameError }"
          :aria-invalid="!!nameError"
          :aria-describedby="nameError ? 'name-error' : 'name-hint'"
          @input="nameError = ''"
          @keyup.enter="save"
          ref="nameInput"
          placeholder="Enter a name to identify this mapping"
        />
        <div v-if="nameError" id="name-error" class="invalid-feedback" role="alert">
          {{ nameError }}
        </div>
        <div v-else id="name-hint" class="form-hint">
          Enter a name to identify this mapping
        </div>
      </div>
    </template>

    <template #footer>
      <button class="btn btn-secondary" @click="close" :disabled="saving">
        Cancel
      </button>
      <button
        class="btn btn-primary"
        :disabled="!mappingName.trim() || saving"
        @click="save"
      >
        <span v-if="saving" class="spinner-small" aria-hidden="true"></span>
        {{ saving ? 'Saving...' : 'Save' }}
      </button>
    </template>
  </dcModal>
</template>

<script>
import dcModal from '../../../common/directives/vue-directives/modal/Modal.vue';

export default {
  components: {
    dcModal
  },

  props: {
    show: {
      type: Boolean,
      default: false
    },
    mapping: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      mappingName: '',
      nameError: '',
      saving: false
    }
  },

  mounted() {
    this.$nextTick(() => {
      if (this.$refs.nameInput) {
        this.$refs.nameInput.focus();
      }
    });
  },

  watch: {
    show(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          if (this.$refs.nameInput) {
            this.$refs.nameInput.focus();
          }
        });
      }
    }
  },

  methods: {
    async save() {
      if (!this.mappingName.trim()) {
        this.nameError = 'Mapping name is required';
        return;
      }

      this.saving = true;
      this.$emit('save', this.mappingName.trim());
    },

    close() {
      if (!this.saving) {
        this.$emit('close');
      }
    },

    // Called by parent when save completes (success or failure)
    resetSaving() {
      this.saving = false;
    }
  }
}
</script>

<style scoped lang="scss">
@import '../../../../assets/css/partials/variables';

.modal-title {
  margin: 0;
  font-size: 18px;
  font-weight: 500;
}

.btn-close {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #757575;
  padding: 0;
  line-height: 1;
}

.btn-close:hover {
  color: #333;
}

.form-group {
  margin-bottom: 0;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
}

.form-control {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
}

.form-control:focus {
  outline: none;
  border-color: $green;
  box-shadow: 0 0 0 2px rgba($green, 0.2);
}

.form-control.is-invalid {
  border-color: $red;
}

.form-control.is-invalid:focus {
  box-shadow: 0 0 0 2px rgba($red, 0.2);
}

.form-hint {
  font-size: 12px;
  color: #757575;
  margin-top: 4px;
}

.invalid-feedback {
  font-size: 12px;
  color: $red;
  margin-top: 4px;
}

.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.3s;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-primary {
  background-color: $green;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background-color: $dark-green;
}

.btn-secondary {
  background-color: #f5f5f5;
  color: #757575;
}

.btn-secondary:hover:not(:disabled) {
  background-color: #e0e0e0;
}

.spinner-small {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>

<style lang="scss">
/* Modal container sizing - not scoped */
.mapping-save-modal {
  width: 500px;
  max-width: 90%;
}

.mapping-save-modal .modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid #e0e0e0;
}

.mapping-save-modal .modal-body {
  padding: 16px;
}

.mapping-save-modal .modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 16px;
  border-top: 1px solid #e0e0e0;
}
</style>
